import React, { useState, useEffect, useRef } from 'react';
import './PdfStep.css';

export default function PdfStep({ url, title }) {
    const [pdfjsLoaded, setPdfjsLoaded] = useState(false);
    const [pdf, setPdf] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [numPages, setNumPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [zoom, setZoom] = useState(1.0);
    const canvasRef = useRef(null);
    const renderTaskRef = useRef(null);
    const containerRef = useRef(null);

    // Dynamically load PDF.js from CDN to avoid huge package bundle size
    useEffect(() => {
        if (window.pdfjsLib) {
            setPdfjsLoaded(true);
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        script.async = true;
        script.onload = () => {
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
            setPdfjsLoaded(true);
        };
        script.onerror = () => {
            setError(true);
            setLoading(false);
        };
        document.body.appendChild(script);
    }, []);

    // Load PDF Document when URL or PDF.js changes
    useEffect(() => {
        if (!pdfjsLoaded || !url) return;
        setLoading(true);
        setError(false);

        const loadingTask = window.pdfjsLib.getDocument(url);
        loadingTask.promise.then(
            (pdfDoc) => {
                setPdf(pdfDoc);
                setNumPages(pdfDoc.numPages);
                setPageNumber(1);
                setLoading(false);
            },
            (err) => {
                console.error('Error loading PDF:', err);
                setError(true);
                setLoading(false);
            }
        );
    }, [pdfjsLoaded, url]);

    // Render current page when pageNumber or zoom changes
    useEffect(() => {
        if (!pdf) return;

        // Cancel previous rendering task if any is currently active
        if (renderTaskRef.current) {
            renderTaskRef.current.cancel();
        }

        pdf.getPage(pageNumber).then((page) => {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const context = canvas.getContext('2d');

            // Calculate responsive scale based on parent container width
            const containerWidth = containerRef.current ? containerRef.current.clientWidth : 800;
            const unscaledViewport = page.getViewport({ scale: 1.0 });
            
            // Adjust calculation based on mobile viewports
            const padding = window.innerWidth <= 576 ? 16 : 48;
            const fitScale = (containerWidth - padding) / unscaledViewport.width;
            
            const finalScale = fitScale * zoom;
            const viewport = page.getViewport({ scale: finalScale });

            // Support high-DPI displays (retina screens) for crisp text rendering
            const pixelRatio = window.devicePixelRatio || 1;
            canvas.width = viewport.width * pixelRatio;
            canvas.height = viewport.height * pixelRatio;
            canvas.style.width = `${viewport.width}px`;
            canvas.style.height = `${viewport.height}px`;

            context.scale(pixelRatio, pixelRatio);

            const renderContext = {
                canvasContext: context,
                viewport: viewport,
            };

            const renderTask = page.render(renderContext);
            renderTaskRef.current = renderTask;

            renderTask.promise.then(
                () => {
                    renderTaskRef.current = null;
                },
                (err) => {
                    if (err.name !== 'RenderingCancelledException') {
                        console.error('Page render error:', err);
                    }
                }
            );
        }).catch(err => {
            console.error('Get page error:', err);
        });
    }, [pdf, pageNumber, zoom, pdfjsLoaded]);

    // Handle container resize to dynamically re-calculate scale
    useEffect(() => {
        if (!pdf) return;
        let resizeTimeout;
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                // Trigger a re-render by setting the zoom back to its state
                setZoom(prev => prev);
            }, 150);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimeout);
        };
    }, [pdf]);

    const handlePrev = () => {
        if (pageNumber > 1) {
            setPageNumber(prev => prev - 1);
        }
    };

    const handleNext = () => {
        if (pageNumber < numPages) {
            setPageNumber(prev => prev + 1);
        }
    };

    if (error) {
        return (
            <div className="pdf-viewer-error">
                <div className="pdf-error-icon">⚠️</div>
                <p>Не вдалося завантажити документ direkt в це вікно.</p>
                <a href={url} target="_blank" rel="noopener noreferrer" className="pdf-btn" style={{ padding: '0.6rem 1.2rem', marginTop: '0.5rem' }}>
                    📥 Відкрити оригінал PDF
                </a>
            </div>
        );
    }

    return (
        <div className="pdf-viewer-container" ref={containerRef}>
            {loading ? (
                <div className="pdf-viewer-loader">
                    <div className="pdf-spinner"></div>
                    <p>Завантаження документа...</p>
                </div>
            ) : (
                <>
                    <div className="pdf-viewer-toolbar">
                        <div className="pdf-toolbar-group">
                            <button className="pdf-btn" onClick={handlePrev} disabled={pageNumber <= 1}>
                                ◀
                            </button>
                            <span className="pdf-page-info">
                                Сторінка <strong>{pageNumber}</strong> з <strong>{numPages}</strong>
                            </span>
                            <button className="pdf-btn" onClick={handleNext} disabled={pageNumber >= numPages}>
                                ▶
                            </button>
                        </div>

                        <div className="pdf-toolbar-group">
                            <button className="pdf-btn" onClick={() => setZoom(z => Math.max(0.5, z - 0.1))} title="Зменшити">
                                🔍-
                            </button>
                            <span className="pdf-zoom-info">{Math.round(zoom * 100)}%</span>
                            <button className="pdf-btn" onClick={() => setZoom(z => Math.min(2.0, z + 0.1))} title="Збільшити">
                                🔍+
                            </button>
                            <button className="pdf-btn" onClick={() => setZoom(1.0)} title="Скинути">
                                🔄
                            </button>
                        </div>
                        
                        <a href={url} target="_blank" rel="noopener noreferrer" className="pdf-btn pdf-btn-download" title="Завантажити оригінал">
                            📥
                        </a>
                    </div>

                    <div className="pdf-canvas-wrapper">
                        <canvas ref={canvasRef} />
                    </div>
                </>
            )}
        </div>
    );
}
